import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';

function Confetti() {
  const particles = useMemo(() => Array.from({ length: 24 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    color: Math.random() > 0.5 ? '#C857A8' : '#F7D7EA',
    duration: 1.5 + Math.random() * 1.3,
    delay: Math.random() * 0.5,
    rotation: Math.random() * 360,
  })), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
      {particles.map(p => (
        <div key={p.id} className="absolute w-1.5 h-1.5 rounded-sm"
          style={{
            left: `${p.left}%`, top: '-10px',
            backgroundColor: p.color,
            transform: `rotate(${p.rotation}deg)`,
            animation: `confettiFall ${p.duration}s ease-in ${p.delay}s forwards`,
          }}
        />
      ))}
    </div>
  );
}

export default function MatchAnimation({ profile, onMessage, onContinue }) {
  const { user } = useAuth();
  const userPhotos = db.getPhotos(user?.id);

  return (
    <div className="fixed inset-0 z-50 bg-[#FAF8F5] flex flex-col items-center justify-center px-6" data-testid="match-animation">
      <Confetti />

      <div className="flex items-center relative z-20 mb-6">
        {/* User photo */}
        <div className="w-[90px] h-[90px] rounded-full overflow-hidden border-[3px] border-[#C857A8] animate-match-left">
          {userPhotos[0] ? (
            <img src={userPhotos[0].url} className="w-full h-full object-cover" alt="You" />
          ) : (
            <div className="w-full h-full bg-saffron-light flex items-center justify-center text-[#9A3D81] font-bold text-2xl">You</div>
          )}
        </div>
        {/* Match photo - overlaps 16px */}
        <div className="w-[90px] h-[90px] rounded-full overflow-hidden border-[3px] border-[#C857A8] -ml-4 animate-match-right">
          {profile.photos?.[0] ? (
            <img src={profile.photos[0].url} className="w-full h-full object-cover" alt={profile.display_name} />
          ) : (
            <div className="w-full h-full bg-saffron-light flex items-center justify-center text-[#9A3D81] font-bold text-2xl">{profile.display_name?.[0]}</div>
          )}
        </div>
      </div>

      <h2 className="text-[28px] font-semibold text-[#C857A8] text-center relative z-20" data-testid="match-text">
        It's a match
      </h2>
      <p className="text-[15px] text-[#7A7672] mt-2 text-center relative z-20">
        You and {profile.display_name} liked each other
      </p>

      <div className="mt-8 flex flex-col gap-3 w-full max-w-xs relative z-20">
        <Button onClick={onMessage} className="h-[52px] text-[15px] font-semibold rounded-[26px] bg-[#C857A8] hover:bg-saffron-mid" data-testid="send-message-btn">
          Send a message
        </Button>
        <Button variant="outline" onClick={onContinue} className="h-[52px] text-[15px] font-semibold rounded-[26px] border-[#C857A8] text-[#C857A8]" data-testid="keep-exploring-btn">
          Keep exploring
        </Button>
      </div>
    </div>
  );
}
